import { FetchMoreOptions, QueryHookOptions } from "@apollo/client";
import {
  GetEquipmentPiecesQuery,
  GetEquipmentPiecesQueryVariables,
  useGetEquipmentPiecesQuery,
} from "generated/graphql";
import { useCallback, useMemo } from "react";

export const cacheKey = "GetEquipmentPieces";

export default function useGetEquipmentPieces(
  { page = 1, first = 10, search, sort }: GetEquipmentPiecesQueryVariables,
  baseOptions: QueryHookOptions<
    GetEquipmentPiecesQuery,
    GetEquipmentPiecesQueryVariables
  > = undefined
) {
  const {
    data: graphqlData,
    loading,
    error,
    fetchMore: more,
  } = useGetEquipmentPiecesQuery({
    variables: {
      page,
      first,
      search,
      sort,
    },
    ...baseOptions,
  });

  const fetchMore = useCallback(
    async ({
      page,
      updateQuery,
    }: {
      page: number;
      updateQuery?: FetchMoreOptions<
        GetEquipmentPiecesQuery,
        GetEquipmentPiecesQueryVariables
      >["updateQuery"];
    }) => {
      return more({
        variables: { page },
        updateQuery,
      });
    },
    [more]
  );

  const getData = useCallback(({ data }: { data?: GetEquipmentPiecesQuery }) => {
    return data?.getEquipmentPieces?.data || null;
  }, []);

  const getPaginatorInfo = useCallback(({ data }: { data?: GetEquipmentPiecesQuery }) => {
    return data?.getEquipmentPieces?.paginatorInfo || null;
  }, []);

  const data = useMemo(() => {
    return getData({ data: graphqlData });
  }, [graphqlData, getData]);

  const paginatorInfo = useMemo(() => {
    return getPaginatorInfo({ data: graphqlData });
  }, [graphqlData]);

  return {
    data,
    loading,
    error,
    paginatorInfo,
    fetchMore,
    getData,
    getPaginatorInfo,
  } as const;
}
